import { httpClient } from './http-client';
import {
  institutionInitials,
  monogramColorIndex,
  normalizeInstitutionName,
} from '../utils/institution-monogram';

/**
 * Where a mark's imagery came from.
 *
 * 'site' is imagery our server fetched from the institution's own website and
 * cached (migration 053). It is the source for most branded rows -- Plaid
 * carries a logo for only 20 of 198 Canadian institutions.
 */
export type BrandingSource = 'plaid' | 'site' | 'catalog' | 'derived';

/** Branding for one institution, as returned by GET /institutions/branding. */
export interface InstitutionBranding {
  /** The exact string that was asked about. */
  query: string;
  /** Canonical institution name, or the query when nothing matched. */
  name: string;
  /**
   * Base64 image data, no data: prefix. Inlined rather than referenced by URL
   * so the browser never issues a request that would reveal which banks this
   * user holds. Absent is still common.
   */
  logo?: string;
  /**
   * What `logo` actually is, sniffed server-side from the bytes. Needed to
   * build a correct data: URI -- a site-fetched icon is often an ICO or GIF,
   * and one announced as image/png will not render.
   */
  logo_content_type?: string;
  primary_color?: string;
  url?: string;
  initials: string;
  color_index: number;
  source: BrandingSource;
}

interface BrandingResponse {
  branding: Record<string, InstitutionBranding>;
}

/**
 * Resolved branding, keyed by the normalised name.
 *
 * Module-level and never evicted: the catalogue is a few dozen institutions
 * per household and a logo is ~7KB of base64. Keyed on the NORMALISED name so
 * "TD Chequing" and "td chequing" share one entry, and cleared only by a
 * reload — which is the right cadence for data that changes yearly.
 */
const cache = new Map<string, InstitutionBranding>();

/** Names queued for the next batched request, and everyone waiting on them. */
let queue = new Set<string>();
let waiters: Array<() => void> = [];
let flushHandle: ReturnType<typeof setTimeout> | null = null;

/**
 * Branding derived without the network.
 *
 * Always available and always drawable, which is the whole point: a page must
 * never render an empty square while a lookup is in flight.
 *
 * @param name Institution or account name.
 */
export function localBranding(name: string): InstitutionBranding {
  return {
    query: name,
    name,
    initials: institutionInitials(name),
    color_index: monogramColorIndex(name),
    source: 'derived',
  };
}

/** Cached branding for a name, or undefined if it has not been resolved. */
export function cachedBranding(name: string): InstitutionBranding | undefined {
  return cache.get(normalizeInstitutionName(name));
}

/**
 * Seed the cache from a response that already carried branding.
 *
 * The connections endpoint inlines branding for the institutions it lists, so
 * that page has no reason to ask again.
 *
 * @param entries Branding keyed by the name it was resolved for.
 */
export function primeBranding(entries?: Record<string, InstitutionBranding>): void {
  if (!entries) return;
  for (const [key, value] of Object.entries(entries)) {
    if (value) cache.set(normalizeInstitutionName(key), value);
  }
}

/** Drop everything. Exists for tests; nothing in the app needs it. */
export function resetBrandingCache(): void {
  cache.clear();
  queue = new Set();
  waiters = [];
  if (flushHandle) {
    clearTimeout(flushHandle);
    flushHandle = null;
  }
}

/**
 * Send one request for everything queued this tick.
 *
 * Batching matters because the mark is a per-row component: a twelve-account
 * page would otherwise open twelve connections to resolve three banks.
 */
async function flush(): Promise<void> {
  flushHandle = null;
  const names = Array.from(queue);
  queue = new Set();
  const woken = waiters;
  waiters = [];

  if (names.length > 0) {
    try {
      const params = names.map((n) => `name=${encodeURIComponent(n)}`).join('&');
      const res = await httpClient.get<BrandingResponse>(`/institutions/branding?${params}`);
      for (const [key, value] of Object.entries(res?.branding ?? {})) {
        if (value) cache.set(normalizeInstitutionName(key), value);
      }
    } catch {
      // Swallowed on purpose. A failed branding lookup must not surface as an
      // error anywhere: the caller falls back to the monogram it was already
      // drawing. Caching the derived result stops a dead endpoint from being
      // retried once per row.
      for (const name of names) {
        if (!cache.has(normalizeInstitutionName(name))) {
          cache.set(normalizeInstitutionName(name), localBranding(name));
        }
      }
    }
  }

  woken.forEach((resolve) => resolve());
}

/**
 * Resolve branding for a name, using the cache and batching the rest.
 *
 * @param name Institution or account name; blank names resolve locally.
 * @returns Branding — the monogram fallback when nothing matches.
 */
export async function resolveBranding(name: string): Promise<InstitutionBranding> {
  const key = normalizeInstitutionName(name);
  if (!key) return localBranding(name);

  const hit = cache.get(key);
  if (hit) return hit;

  queue.add(name);
  await new Promise<void>((resolve) => {
    waiters.push(resolve);
    if (flushHandle === null) {
      // A macrotask, not a microtask: components mount in the same tick but
      // across several microtask turns, and the goal is to catch all of them.
      flushHandle = setTimeout(flush, 0);
    }
  });

  return cache.get(key) ?? localBranding(name);
}
